/**
 * mobilePayment.js — Utility functions for mobile in-app purchase flow.
 *
 * App passes a device_id via passthrough.  Paddle collects the user's email
 * on its own checkout form.  After payment, the deep link carries the email
 * back to the App so it can query entitlements.
 *
 * Flow:
 *   App → opens browser → /mobile-checkout?passthrough=DEVICE_ID&plan=annual
 *       → Paddle Checkout (user types email on Paddle's form)
 *       → checkout.completed → customer.email extracted from event
 *       → deep link: wordcloudstudio://payment-success?email=user@typed.com
 *       → App queries GET /api/entitlement?email=user@typed.com
 */

const DEEP_LINK_SCHEME = 'wordcloudstudio'

/**
 * Parse the passthrough value from query params.
 * This is a device or app identifier — NOT the user's email.
 * The email is collected later by Paddle's checkout form.
 *
 * @returns {string | null}
 */
export function getPassthroughId() {
  const params = new URLSearchParams(window.location.search)
  return params.get('passthrough') || null
}

/**
 * Legacy alias kept for backward compatibility.
 * @deprecated use getPassthroughId
 */
export const getPassthroughEmail = getPassthroughId

/**
 * Legacy alias.
 * @deprecated use getPassthroughId
 */
export const getPassthroughUserId = getPassthroughId

/**
 * Parse the selected plan from query params (optional — defaults to annual).
 *
 * @returns {'annual' | 'monthly' | null}
 */
export function getSelectedPlan() {
  const params = new URLSearchParams(window.location.search)
  return params.get('plan') || null
}

/**
 * Build a deep-link URL that the mobile app listens for.
 *
 * @param {'payment-success' | 'payment-cancelled' | 'payment-error'} event
 * @param {Object} [params] - Extra query params to pass to the app
 * @returns {string} e.g. wordcloudstudio://payment-success?email=user@gmail.com&txn=xxx
 */
export function buildDeepLink(event, params = {}) {
  const url = new URL(`${DEEP_LINK_SCHEME}://${event}`)
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) {
      url.searchParams.set(k, String(v))
    }
  })
  return url.toString()
}

/**
 * Attempt to open the app via deep link.
 *
 * @param {string} url - Deep link URL
 * @param {number} [timeout=2500] - ms before considering the deep link as failed
 * @returns {Promise<boolean>} true if deep link likely succeeded
 */
export function openDeepLink(url, timeout = 2500) {
  return new Promise((resolve) => {
    let resolved = false

    const handleBlur = () => { if (!resolved) { resolved = true; cleanup(); resolve(true) } }
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden' && !resolved) { resolved = true; cleanup(); resolve(true) }
    }
    const cleanup = () => {
      window.removeEventListener('blur', handleBlur)
      document.removeEventListener('visibilitychange', handleVisibility)
      clearTimeout(timer)
    }

    window.addEventListener('blur', handleBlur, { once: true })
    document.addEventListener('visibilitychange', handleVisibility)

    const timer = setTimeout(() => { if (!resolved) { resolved = true; cleanup(); resolve(false) } }, timeout)

    window.location.href = url
  })
}

export function stashPendingTransaction(data) {
  sessionStorage.setItem('pending_mobile_txn', JSON.stringify(data))
}

export function getPendingTransaction() {
  try {
    const raw = sessionStorage.getItem('pending_mobile_txn')
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

export function clearPendingTransaction() {
  sessionStorage.removeItem('pending_mobile_txn')
}

export default {
  getPassthroughId,
  getPassthroughEmail,
  getPassthroughUserId,
  getSelectedPlan,
  buildDeepLink,
  openDeepLink,
  stashPendingTransaction,
  getPendingTransaction,
  clearPendingTransaction,
}
